import '@apps/web/routes/_app/admin/agent/theme.css';

import { createFileRoute, Link } from '@tanstack/react-router';
import {
    ArrowLeft,
    CheckCircle2,
    Clock3,
    Database,
    RefreshCw,
} from 'lucide-react';
import {
    type ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { z } from 'zod';

import { Button } from '@apps/web/components/ui/button';
import { AdminAnalyticsChatRail } from '@apps/web/features/admin_analytics/admin_analytics_chat_rail';
import {
    captureAdminPageSnapshotPairs,
    findNewestCreatedSnapshotPair,
} from '@apps/web/features/admin_analytics/admin_analytics_run_changes';
import { resolveAdminAnalyticsSnapshotCanvasState } from '@apps/web/features/admin_analytics/admin_analytics_snapshot_canvas_state';
import { AdminAnalyticsWorkspaceLayout } from '@apps/web/features/admin_analytics/admin_analytics_workspace_layout';
import {
    buildAdminPageSourceDocument,
    PAGE_FRAME_PERMISSIONS,
} from '@apps/web/lib/admin_page_worker_runtime';
import { getApiClient } from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';
import { useApiQuery } from '@apps/web/swr_data_fetching/use_api_query';
import { type ApiResponseType } from '@libraries/typescript/api_client';

const pageSearchSchema = z.object({
    versionId: z.string().uuid().optional().catch(undefined),
    snapshotId: z.string().uuid().optional().catch(undefined),
});

export const Route = createFileRoute('/_app/admin/analytics/$slug')({
    component: AdminAnalyticsDetail,
    validateSearch: pageSearchSchema,
    head: () => ({
        meta: [{ title: 'Analytics Dashboard | Phoebe Admin' }],
    }),
});

type Page = ApiResponseType<'/admin/agent/pages/{slug}', 'get'>;
type Version = NonNullable<Page['versions']>[number];
type Snapshot = NonNullable<Version['snapshots']>[number];
type Content = ApiResponseType<
    '/admin/agent/pages/{slug}/versions/{version_id}/snapshots/{snapshot_id}/content',
    'get'
>;
type InteractiveContent = ApiResponseType<
    '/admin/agent/pages/{slug}/versions/{version_id}/snapshots/{snapshot_id}/query',
    'post'
>;

type SelectedPair = {
    readonly versionId: string;
    readonly snapshotId: string;
};

function AdminAnalyticsDetail() {
    const { slug } = Route.useParams();
    const search = Route.useSearch();
    const navigate = Route.useNavigate();
    const apiClient = getApiClient();
    const [selectedPair, setSelectedPair] = useState<SelectedPair | null>(null);
    const [interactiveContent, setInteractiveContent] =
        useState<InteractiveContent | null>(null);
    const [parameterValues, setParameterValues] = useState<
        Record<string, unknown>
    >({});
    const [isQuerying, setIsQuerying] = useState(false);
    const [isActivating, setIsActivating] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const snapshotPairsAtRunStart = useRef<ReadonlySet<string> | null>(null);
    const {
        data: page,
        error,
        isLoading,
        mutate,
    } = useApiQuery({
        path: '/admin/agent/pages/{slug}',
        method: 'get',
        pathParams: { slug },
        select: (response) => response,
    });

    const selectPair = useCallback(
        (pair: SelectedPair, replace = true) => {
            setSelectedPair(pair);
            setInteractiveContent(null);
            void navigate({
                search: {
                    versionId: pair.versionId,
                    snapshotId: pair.snapshotId,
                },
                replace,
            });
        },
        [navigate],
    );

    useEffect(() => {
        if (!page) {
            return;
        }
        const requested =
            search.versionId && search.snapshotId
                ? {
                      versionId: search.versionId,
                      snapshotId: search.snapshotId,
                  }
                : null;
        const requestedExists = requested
            ? (page.versions ?? []).some(
                  (version) =>
                      version.id === requested.versionId &&
                      (version.snapshots ?? []).some(
                          (snapshot) => snapshot.id === requested.snapshotId,
                      ),
              )
            : false;
        if (requested && requestedExists) {
            if (
                selectedPair?.versionId !== requested.versionId ||
                selectedPair.snapshotId !== requested.snapshotId
            ) {
                setSelectedPair(requested);
            }
            return;
        }
        if (
            selectedPair &&
            (page.versions ?? []).some(
                (version) =>
                    version.id === selectedPair.versionId &&
                    (version.snapshots ?? []).some(
                        (snapshot) => snapshot.id === selectedPair.snapshotId,
                    ),
            )
        ) {
            return;
        }
        const active =
            page.active_version_id && page.active_snapshot_id
                ? {
                      versionId: page.active_version_id,
                      snapshotId: page.active_snapshot_id,
                  }
                : null;
        const newestVersionWithSnapshot = page.versions?.find(
            (version) => version.snapshots?.[0],
        );
        const newestSnapshot = newestVersionWithSnapshot?.snapshots?.[0];
        const fallback =
            active ??
            (newestVersionWithSnapshot && newestSnapshot
                ? {
                      versionId: newestVersionWithSnapshot.id,
                      snapshotId: newestSnapshot.id,
                  }
                : null);
        if (fallback) {
            selectPair(fallback);
        }
    }, [page, search.snapshotId, search.versionId, selectPair, selectedPair]);

    const { data: content, error: contentError } = useApiQuery({
        path: '/admin/agent/pages/{slug}/versions/{version_id}/snapshots/{snapshot_id}/content',
        method: 'get',
        pathParams: {
            slug,
            version_id: selectedPair?.versionId ?? '',
            snapshot_id: selectedPair?.snapshotId ?? '',
        },
        enabled: selectedPair !== null,
        select: (response) => response,
    });

    useEffect(() => {
        if (!content) {
            return;
        }
        setInteractiveContent(null);
        setParameterValues(
            Object.fromEntries(
                content.controls.map((control) => [
                    control.name,
                    control.value,
                ]),
            ),
        );
    }, [content]);

    const selectedVersion = page?.versions?.find(
        (version) => version.id === selectedPair?.versionId,
    );
    const selectedSnapshot = selectedVersion?.snapshots?.find(
        (snapshot) => snapshot.id === selectedPair?.snapshotId,
    );
    const isSelectedActive =
        page?.active_version_id === selectedPair?.versionId &&
        page?.active_snapshot_id === selectedPair?.snapshotId;
    const displayedContent: Content | InteractiveContent | undefined =
        interactiveContent ?? content;
    const hasAvailableSnapshot = (page?.versions ?? []).some(
        (version) => (version.snapshots?.length ?? 0) > 0,
    );
    const snapshotCanvasState = resolveAdminAnalyticsSnapshotCanvasState({
        hasAvailableSnapshot,
        hasSelectedSnapshot: selectedPair !== null,
        hasDisplayedContent: displayedContent !== undefined,
        hasContentError: contentError !== undefined,
    });
    const pageFacts = {
        kind: 'admin_page' as const,
        page_id: page?.id ?? '',
        slug,
        title: page?.title ?? slug,
        data_source:
            selectedSnapshot?.source['kind'] === 'snowflake'
                ? ('snowflake' as const)
                : ('postgres' as const),
        selected_version_id: selectedPair?.versionId ?? null,
        selected_snapshot_id: selectedPair?.snapshotId ?? null,
        active_version_id: page?.active_version_id ?? null,
        active_snapshot_id: page?.active_snapshot_id ?? null,
        query_package_digest: content?.query_package_digest ?? null,
        data_as_of: displayedContent?.data_as_of ?? null,
        control_state: primitiveControlState(parameterValues),
    };

    const rerunDashboard = async () => {
        if (!selectedPair || !content?.query_package_digest || isQuerying) {
            return;
        }
        setIsQuerying(true);
        try {
            const rerun = await apiClient.post({
                url: '/admin/agent/pages/{slug}/versions/{version_id}/snapshots/{snapshot_id}/query',
                pathParams: {
                    slug,
                    version_id: selectedPair.versionId,
                    snapshot_id: selectedPair.snapshotId,
                },
                body: {
                    query_package_digest: content.query_package_digest,
                    parameters: parameterValues,
                },
            });
            setInteractiveContent(rerun);
        } catch (queryError) {
            toast.error(
                queryError instanceof Error
                    ? queryError.message
                    : 'Could not rerun the dashboard',
                { persistNotification: false },
            );
        } finally {
            setIsQuerying(false);
        }
    };

    const activateSelected = async () => {
        if (!page || !selectedPair || isActivating) {
            return;
        }
        setIsActivating(true);
        try {
            await apiClient.post({
                url: '/admin/agent/pages/{slug}/versions/{version_id}/activate',
                pathParams: { slug, version_id: selectedPair.versionId },
                body: {
                    snapshot_id: selectedPair.snapshotId,
                    expected_active_version_id: page.active_version_id,
                    expected_active_snapshot_id: page.active_snapshot_id,
                    client_operation_id: crypto.randomUUID(),
                },
            });
            await mutate();
            toast.success('Published the selected dashboard version', {
                persistNotification: false,
            });
        } catch (activationError) {
            toast.error(
                activationError instanceof Error
                    ? activationError.message
                    : 'Could not publish the dashboard',
                { persistNotification: false },
            );
        } finally {
            setIsActivating(false);
        }
    };

    const refreshActive = async () => {
        if (
            !page?.active_version_id ||
            !page.active_snapshot_id ||
            isRefreshing
        ) {
            return;
        }
        setIsRefreshing(true);
        try {
            const refreshed = await apiClient.post({
                url: '/admin/agent/pages/{slug}/refresh',
                pathParams: { slug },
                body: {
                    expected_active_version_id: page.active_version_id,
                    expected_active_snapshot_id: page.active_snapshot_id,
                    client_operation_id: crypto.randomUUID(),
                },
            });
            await mutate();
            if (refreshed.snapshot_id) {
                selectPair({
                    versionId: refreshed.version_id,
                    snapshotId: refreshed.snapshot_id,
                });
            }
            if (refreshed.outcome === 'failed') {
                toast.error(
                    `Refresh failed (${refreshed.error_code ?? 'unknown'}). The prior snapshot is still active.`,
                    { persistNotification: false },
                );
            }
        } catch (refreshError) {
            toast.error(
                refreshError instanceof Error
                    ? refreshError.message
                    : 'Could not refresh the dashboard',
                { persistNotification: false },
            );
        } finally {
            setIsRefreshing(false);
        }
    };

    const handleAgentRunStarted = () => {
        snapshotPairsAtRunStart.current = captureAdminPageSnapshotPairs(page);
    };

    const handleAgentRunSettled = async () => {
        const previousPairs = snapshotPairsAtRunStart.current;
        snapshotPairsAtRunStart.current = null;
        const refreshed = await mutate();
        const createdPair = findNewestCreatedSnapshotPair(
            previousPairs,
            refreshed,
        );
        if (createdPair) {
            selectPair(createdPair);
        }
    };

    if (isLoading) {
        return <WorkspaceState>Loading dashboard…</WorkspaceState>;
    }
    if (error || !page) {
        return <WorkspaceState>Dashboard could not be loaded.</WorkspaceState>;
    }

    const canvas = (
        <main className="bg-background flex h-full min-h-0 flex-col">
            <header className="flex shrink-0 flex-wrap items-center gap-3 border-b bg-white px-4 py-3">
                <div className="min-w-0 flex-1">
                    <Link
                        to="/admin/analytics"
                        className="text-muted-foreground hover:text-foreground mb-1 inline-flex items-center gap-1 text-[11px]"
                    >
                        <ArrowLeft className="size-3" />
                        Analytics
                    </Link>
                    <h1 className="truncate text-lg font-semibold">
                        {page.title}
                    </h1>
                    <p className="text-muted-foreground truncate text-xs">
                        /admin/analytics/{page.slug}
                    </p>
                </div>
                <Button
                    type="button"
                    variant="outline"
                    onClick={() => void refreshActive()}
                    disabled={
                        !page.active_version_id ||
                        !page.active_snapshot_id ||
                        isRefreshing
                    }
                >
                    <RefreshCw
                        className={
                            isRefreshing ? 'size-4 animate-spin' : 'size-4'
                        }
                    />
                    Refresh
                </Button>
                <Button
                    type="button"
                    onClick={() => void activateSelected()}
                    disabled={!selectedPair || isSelectedActive || isActivating}
                >
                    <CheckCircle2 className="size-4" />
                    {isSelectedActive ? 'Published' : 'Publish'}
                </Button>
            </header>
            {content?.controls.length ? (
                <DashboardControls
                    controls={content.controls}
                    values={parameterValues}
                    onChange={(name, value) =>
                        setParameterValues((current) => ({
                            ...current,
                            [name]: value,
                        }))
                    }
                    onApply={() => void rerunDashboard()}
                    isApplying={isQuerying}
                />
            ) : null}
            <div className="min-h-0 flex-1 overflow-hidden bg-white">
                {snapshotCanvasState === 'error' ? (
                    <div className="text-destructive flex h-full items-center justify-center p-8 text-sm">
                        Verified dashboard content could not be loaded.
                    </div>
                ) : snapshotCanvasState === 'ready' && displayedContent ? (
                    <AdminAnalyticsFrame
                        body={displayedContent.body}
                        presentationRuntime={
                            displayedContent.presentation_runtime
                        }
                        title={page.title}
                    />
                ) : snapshotCanvasState === 'empty' ? (
                    <div className="text-muted-foreground flex h-full items-center justify-center p-8 text-center text-sm">
                        No verified snapshot yet. Ask the agent to build this
                        dashboard to create one.
                    </div>
                ) : (
                    <div className="text-muted-foreground flex h-full items-center justify-center p-8 text-sm">
                        Loading verified snapshot…
                    </div>
                )}
            </div>
        </main>
    );

    return (
        <div className="admin-agent-page h-full min-h-[calc(100svh-3.5rem)] lg:min-h-svh">
            <AdminAnalyticsWorkspaceLayout
                canvas={canvas}
                agent={
                    <AdminAnalyticsChatRail
                        pageSlug={page.slug}
                        pageTitle={page.title}
                        pageFacts={pageFacts}
                        onRunStarted={handleAgentRunStarted}
                        onRunSettled={handleAgentRunSettled}
                    />
                }
                details={
                    <DashboardDetails
                        page={page}
                        version={selectedVersion}
                        snapshot={selectedSnapshot}
                        selectedPair={selectedPair}
                        onSelect={selectPair}
                    />
                }
            />
        </div>
    );
}

function DashboardControls({
    controls,
    values,
    onChange,
    onApply,
    isApplying,
}: {
    readonly controls: Content['controls'];
    readonly values: Record<string, unknown>;
    readonly onChange: (name: string, value: unknown) => void;
    readonly onApply: () => void;
    readonly isApplying: boolean;
}) {
    return (
        <form
            className="flex shrink-0 flex-wrap items-end gap-3 border-b bg-white px-4 py-3"
            onSubmit={(event) => {
                event.preventDefault();
                onApply();
            }}
            data-testid="dashboard-controls"
        >
            {controls.map((control) => (
                <label key={control.name} className="grid gap-1 text-xs">
                    <span className="text-muted-foreground">
                        {control.label}
                    </span>
                    {control.kind === 'select' ? (
                        <select
                            className="border-input bg-background h-9 min-w-44 rounded-md border px-2 text-sm"
                            value={String(values[control.name] ?? '')}
                            onChange={(event) =>
                                onChange(
                                    control.name,
                                    event.currentTarget.value || null,
                                )
                            }
                        >
                            {control.nullable ? (
                                <option value="">All</option>
                            ) : null}
                            {control.options.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    ) : (
                        <input
                            className="border-input bg-background h-9 min-w-40 rounded-md border px-2 text-sm"
                            type={
                                control.kind === 'date'
                                    ? 'date'
                                    : control.kind === 'number'
                                      ? 'number'
                                      : 'text'
                            }
                            value={String(values[control.name] ?? '')}
                            onChange={(event) =>
                                onChange(
                                    control.name,
                                    event.currentTarget.value || null,
                                )
                            }
                        />
                    )}
                </label>
            ))}
            <Button type="submit" variant="outline" disabled={isApplying}>
                <RefreshCw
                    className={isApplying ? 'size-4 animate-spin' : 'size-4'}
                />
                Apply
            </Button>
        </form>
    );
}

function AdminAnalyticsFrame({
    body,
    presentationRuntime,
    title,
}: {
    readonly body: string;
    readonly presentationRuntime: Content['presentation_runtime'];
    readonly title: string;
}) {
    const sourceDocument = useMemo(
        () => buildAdminPageSourceDocument({ body, presentationRuntime }),
        [body, presentationRuntime],
    );
    return (
        <iframe
            title={title}
            sandbox={
                presentationRuntime === 'static_html_v1' ? '' : 'allow-scripts'
            }
            allow={PAGE_FRAME_PERMISSIONS}
            referrerPolicy="no-referrer"
            srcDoc={sourceDocument}
            className="h-full min-h-[620px] w-full border-0"
            data-testid="admin-analytics-frame"
        />
    );
}

function DashboardDetails({
    page,
    version,
    snapshot,
    selectedPair,
    onSelect,
}: {
    readonly page: Page;
    readonly version: Version | undefined;
    readonly snapshot: Snapshot | undefined;
    readonly selectedPair: SelectedPair | null;
    readonly onSelect: (pair: SelectedPair) => void;
}) {
    return (
        <div className="space-y-5 p-4">
            <section>
                <h2 className="text-sm font-semibold">Selected snapshot</h2>
                {version && snapshot ? (
                    <dl className="mt-3 grid grid-cols-[88px_minmax(0,1fr)] gap-2 text-xs">
                        <dt className="text-muted-foreground">Version</dt>
                        <dd>v{version.version}</dd>
                        <dt className="text-muted-foreground">Snapshot</dt>
                        <dd>#{snapshot.refresh_sequence}</dd>
                        <dt className="text-muted-foreground">Data as of</dt>
                        <dd>{formatTimestamp(snapshot.data_as_of)}</dd>
                        <dt className="text-muted-foreground">Rows</dt>
                        <dd>{snapshot.row_count}</dd>
                        <dt className="text-muted-foreground">Source</dt>
                        <dd>{String(snapshot.source['label'] ?? '—')}</dd>
                        <dt className="text-muted-foreground">State</dt>
                        <dd>
                            {version.id === page.active_version_id &&
                            snapshot.id === page.active_snapshot_id
                                ? 'Published'
                                : 'Draft / historical'}
                        </dd>
                    </dl>
                ) : (
                    <p className="text-muted-foreground mt-2 text-xs">
                        No snapshot selected.
                    </p>
                )}
            </section>
            <section className="border-t pt-4">
                <h2 className="text-sm font-semibold">History</h2>
                <div className="mt-3 space-y-4">
                    {(page.versions ?? []).map((candidateVersion) => (
                        <div key={candidateVersion.id}>
                            <div className="flex items-center justify-between text-xs">
                                <span className="font-medium">
                                    Presentation v{candidateVersion.version}
                                </span>
                                {candidateVersion.query_package_sha256 ? (
                                    <span
                                        className="text-muted-foreground font-mono"
                                        title={
                                            candidateVersion.query_package_sha256
                                        }
                                    >
                                        SQL package{' '}
                                        {candidateVersion.query_package_sha256.slice(
                                            0,
                                            8,
                                        )}
                                    </span>
                                ) : null}
                            </div>
                            <div className="mt-2 space-y-1">
                                {(candidateVersion.snapshots ?? []).map(
                                    (candidateSnapshot) => {
                                        const selected =
                                            candidateVersion.id ===
                                                selectedPair?.versionId &&
                                            candidateSnapshot.id ===
                                                selectedPair.snapshotId;
                                        return (
                                            <button
                                                type="button"
                                                key={candidateSnapshot.id}
                                                onClick={() =>
                                                    onSelect({
                                                        versionId:
                                                            candidateVersion.id,
                                                        snapshotId:
                                                            candidateSnapshot.id,
                                                    })
                                                }
                                                className={
                                                    'flex w-full items-center gap-2 rounded-md border p-2 text-left text-xs ' +
                                                    (selected
                                                        ? 'border-foreground bg-muted'
                                                        : 'hover:bg-muted/50 border-transparent')
                                                }
                                            >
                                                <Database className="size-3.5" />
                                                <span className="min-w-0 flex-1">
                                                    Snapshot #
                                                    {
                                                        candidateSnapshot.refresh_sequence
                                                    }
                                                    <span className="text-muted-foreground mt-0.5 flex items-center gap-1">
                                                        <Clock3 className="size-3" />
                                                        {formatTimestamp(
                                                            candidateSnapshot.data_as_of,
                                                        )}
                                                    </span>
                                                </span>
                                            </button>
                                        );
                                    },
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </section>
            {snapshot?.caveats.length ? (
                <section className="border-t pt-4">
                    <h2 className="text-sm font-semibold">Caveats</h2>
                    <ul className="text-muted-foreground mt-2 list-disc space-y-1 pl-4 text-xs">
                        {(snapshot.caveats ?? []).map((caveat: string) => (
                            <li key={caveat}>{caveat}</li>
                        ))}
                    </ul>
                </section>
            ) : null}
        </div>
    );
}

function formatTimestamp(value: string) {
    return new Intl.DateTimeFormat(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
    }).format(new Date(value));
}

function primitiveControlState(values: Record<string, unknown>) {
    return Object.fromEntries(
        Object.entries(values).filter(
            (entry): entry is [string, string | number | boolean | null] =>
                entry[1] === null ||
                typeof entry[1] === 'string' ||
                typeof entry[1] === 'number' ||
                typeof entry[1] === 'boolean',
        ),
    );
}

function WorkspaceState({ children }: { readonly children: ReactNode }) {
    return (
        <main className="text-muted-foreground flex min-h-svh items-center justify-center p-8 text-sm">
            {children}
        </main>
    );
}
